import { Box } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import Chart from "react-apexcharts";

import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMBarChartCardProps extends Omit<PMBaseCardProps, "children"> {
  /** Card title */
  title: string;
  /**
   * Y-axis values:
   * - if `chartType = 1`: a flat number[] for a single series
   * - if `chartType = 2` or `3`: an array of number[] arrays, one per series
   */
  data: number[] | number[][];
  /** X-axis labels (must match the length of each data series) */
  categories: (string | number)[];
  /**
   * 1 = single-series bar
   * 2 = two-series grouped bar
   * 3 = three-series stacked bar (overlapping)
   * @default 1
   */
  chartType?: 1 | 2 | 3;
  /** X-axis label */
  xLabel: string;
  /** Y-axis label */
  yLabel: string;
  /** One or more stroke/fill colors */
  colors?: string | string[];
  /** Optional names to show in the legend */
  seriesNames?: string[];
  /** Render bars horizontally so categories are shown on the Y-axis */
  horizontal?: boolean;
  /** Should the totals of stacked bar charts (chartType 3) be shown above the bars */
  showTotals?: boolean;
  /** Group x axis labels */
  group?: {
    style: {
      fontSize: string;
      fontWeight: number | string;
    };
    groups: { title: string; cols: number }[];
  };
}

export type PMBarChartCardEventName = "segment-selected";

export type PMBarChartCardEventPayload = {
  category: string | number;
  categoryIndex: number;
  seriesIndex: number;
  seriesName: string;
};

export const PMBarChartCard: PimoReactComponent<
  PMBarChartCardProps,
  PMBarChartCardEventName,
  PMBarChartCardEventPayload
> = ({
  title,
  data,
  categories,
  chartType = 1,
  xLabel,
  yLabel,
  group,
  horizontal = false,
  showTotals = false,
  colors = "#2C3D4F",
  seriesNames,
  icon = "BarChart",
  showInfoIcon = false,
  showMenuIcon = false,
  fixedWidth = "100%",
  fixedHeight = "300px",
  fireEvent,
  ...rest
}) => {
  const allSeries: number[][] =
    chartType === 1
      ? [data as number[]]
      : Array.isArray(data[0])
        ? (data as number[][])
        : [];

  const defaultNames = allSeries.map((_, i) => `Series ${i + 1}`);
  const names = seriesNames?.slice(0, allSeries.length) ?? defaultNames;

  const series = names.map((name, i) => ({
    name,
    data: allSeries[i],
  }));

  const palette = Array.isArray(colors) ? colors : [colors];
  const useDistributedSingleSeriesColors =
    chartType === 1 && series.length === 1 && palette.length > 1;
  const chartColors = useDistributedSingleSeriesColors
    ? palette
    : palette.slice(0, series.length);

  const options = {
    chart: {
      type: "bar" as const,
      stacked: chartType === 3,
      toolbar: { show: false },
      zoom: { enabled: false },
      events: {
        dataPointSelection: (
          _event: MouseEvent,
          _chartContext: ApexCharts | undefined,
          config: ApexCharts.ApexChartEventOpts
        ) => {
          const categoryIndex = config.dataPointIndex;
          const seriesIndex = config.seriesIndex;
          const category = categories[categoryIndex];
          const seriesName = names[seriesIndex];
          if (category !== undefined && seriesName !== undefined) {
            fireEvent?.("segment-selected", {
              category,
              categoryIndex,
              seriesIndex,
              seriesName,
            });
          }
        },
      },
    },
    plotOptions: {
      bar: {
        ...(chartType === 3 && showTotals
          ? {
              dataLabels: {
                total: {
                  enabled: true,
                  style: {
                    fontSize: "13px",
                    fontWeight: 900,
                  },
                },
              },
            }
          : {}),
        horizontal,
        distributed: useDistributedSingleSeriesColors,
        columnWidth: chartType === 3 ? "60%" : "50%",
      },
    },
    colors: chartColors,
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 1,
    },
    xaxis: {
      categories,
      title: { text: xLabel },
      ...(group != null
        ? {
            group,
          }
        : {}),
    },
    yaxis: {
      title: { text: yLabel },
      labels: {
        // avoid floating-point noise in axis ticks
        formatter: (value: number) =>
          (Math.round(value * 100) / 100).toString(),
      },
    },
    legend: {
      show: series.length > 1,
      position: "top" as const,
    },
    tooltip: {
      enabled: true,
    },
  } as const;

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      fixedWidth={fixedWidth}
      fixedHeight={fixedHeight}
      {...rest}
    >
      <Box sx={{ width: "100%", height: "100%" }}>
        <Chart options={options} series={series} type="bar" height="100%" />
      </Box>
    </PMBaseCard>
  );
};

export default PMBarChartCard;
